<template>
  <div>
    <div class="top-banner">{{ $t('message.credit.inventoryMatching.yourVehicle') }}</div>
    <div class="content-container">
      <h2 data-test="vehicleTitle">
        {{ vehicleName }} - {{ $n(vehicle.price, 'currencyDollars', languageCode) }}<sup>*</sup>
      </h2>
      <div class="subtitle">{{ vehicle.selectedOptions }}</div>
      <image-carousel class="mt-4 mb-5" :image-list="vehicle.photoURLs"> </image-carousel>
      <ul class="table-list my-4">
        <li v-for="(item, index) in listItems" :key="index">
          <span class="item-label">{{ $t(`message.credit.inventoryMatching.${item.label}`) }}</span>
          <span class="item">{{ vehicle[`${item.value}`] }}</span>
        </li>
        <li class="d-sm-none" v-if="listItems.length % 2 !== 0"></li>
        <li class="d-none d-sm-block" v-if="listItems.length % 3 !== 0"></li>
        <li class="d-none d-sm-block" v-if="listItems.length % 3 !== 0 && (listItems.length + 1) % 3 !== 0"></li>
      </ul>
      <div v-if="vehicle.dealerName" class="dealer-location mt-3 mb-4">
        {{ $t('message.credit.inventoryMatching.availableAt') }}
        <span class="dealer-name">{{ vehicle.dealerName }}</span>
      </div>
      <DropDownCardV2 v-if="vehicle.shortDescription" :cardStyle="false" :defaultOpen="true" class="mb-4">
        <template v-slot:label>
          <div class="py-3 border-bottom">
            <h4 class="m-0">{{ $t('message.credit.inventoryMatching.description') }}</h4>
          </div>
        </template>
        <template v-slot:dropdown>
          <div class="small-copy">
            <p v-html-safe="vehicle.shortDescription"></p>
          </div>
        </template>
      </DropDownCardV2>
      <DropDownCardV2 v-if="vehicle.highlight" :cardStyle="false" class="mb-4">
        <template v-slot:label>
          <div class="py-3 border-bottom">
            <h4 class="m-0">{{ $t('message.credit.inventoryMatching.keyFeatures') }}</h4>
          </div>
        </template>
        <template v-slot:dropdown>
          <div class="small-copy">
            <p v-html-safe="vehicle.highlight"></p>
          </div>
        </template>
      </DropDownCardV2>
      <DropDownCardV2 v-if="vehicle.equipment" :cardStyle="false" class="mb-4">
        <template v-slot:label>
          <div class="py-3 border-bottom">
            <h4 class="m-0">{{ $t('message.credit.inventoryMatching.technicalSpecifications') }}</h4>
          </div>
        </template>
        <template v-slot:dropdown>
          <div class="small-copy">
            <p v-html-safe="vehicle.equipment"></p>
          </div>
        </template>
      </DropDownCardV2>
      <div class="font-size-10 color-primary-grey">{{ getPriceTaxDisclaimerMessage }}</div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import helperFunctions from '@/utils/helperFunctions';
import DropDownCardV2 from '@/components/DropDownCardV2.vue';
import ImageCarousel from '@/components/ImageCarousel.vue';

export default {
  name: 'VehicleDetails',
  components: {
    ImageCarousel,
    DropDownCardV2,
  },
  data() {
    return {
      listItems: [
        {
          label: 'bodyStyle',
          value: 'category',
        },
        {
          label: 'exterior',
          value: 'exteriorColor',
        },
        {
          label: 'transmission',
          value: 'transmission',
        },
        {
          label: 'engine',
          value: 'engine',
        },
        {
          label: 'interior',
          value: 'interiorColor',
        },
        {
          label: 'drivetrain',
          value: 'drivetrain',
        },
        {
          label: 'stock#',
          value: 'stockNumber',
        },
        {
          label: 'vin',
          value: 'identifier',
        },
      ],
    };
  },
  computed: {
    ...mapGetters({
      languageCode: 'user/getLanguageCodeForNumericalInternationalization',
      integrationProvince: 'business/getIntegrationProvince',
    }),
    vehicleData() {
      return this.$store.state.credit.matchedInventoryResults.matchedVehicles.filter(
        vehicle => vehicle.id === this.$route.params.id
      )[0];
    },
    vehicle() {
      return this.vehicleData.vehicle;
    },
    vehicleName() {
      return helperFunctions.getVehicleName(this.vehicle);
    },
    getPriceTaxDisclaimerMessage() {
      const provinceDisclaimer = helperFunctions.getDisclaimerByProvince(this.integrationProvince);
      return this.$t(provinceDisclaimer, { prefix: '* ', postfix: '' });
    },
  },
};
</script>

<style lang="scss" scoped>
@import 'src/assets/scss/variables';

.top-banner {
  position: absolute;
  top: 45px;
  right: 0;
  width: 100%;
  background-color: $primary;
  color: #fff;
  font-size: 20px;
  font-style: normal;
  font-weight: 700;
  line-height: 25px;
  padding: 16px 15px;
  text-align: center;
  @media only screen and (max-width: $breakpoint-mobile) {
    font-size: 18px;
    line-height: 22.5px;
  }
}

.content-container {
  padding: 65px 0 150px;
}

.subtitle {
  color: #4b5863;
  font-size: 20px;
  font-weight: 700;
  line-height: 25px;
  @media only screen and (max-width: $breakpoint-mobile) {
    font-size: 18px;
    line-height: 22.5px;
  }
}

.table-list {
  position: relative;
  border-top: 1px solid #dae2ea;
  table-layout: fixed;
  list-style: none;
  display: flex;
  flex-wrap: wrap;
  margin: 0;
  padding: 0;
  li {
    border-bottom: 1px solid #dae2ea;
    margin: 0;
    padding: 6px 8px;
    width: calc(100% / 3);
    min-height: 55px;
  }
  li:nth-child(4),
  li:nth-child(5),
  li:nth-child(6) {
    background-color: #f0f4f7;
  }
  .item-label {
    color: #4b5863;
    font-size: 12px;
    line-height: 18px;
    display: block;
  }
  .item {
    color: #19222b;
    font-size: 14px;
    font-weight: 700;
    line-height: 17.5px;
  }
  @media only screen and (max-width: $breakpoint-mobile) {
    li {
      width: 50%;
    }
    li:nth-child(3),
    li:nth-child(4),
    li:nth-child(7),
    li:nth-child(8) {
      background-color: #f0f4f7;
    }
    li:nth-child(5),
    li:nth-child(6) {
      background-color: initial;
    }
  }
}

.small-copy::v-deep,
.small-copy::v-deep * {
  color: #4b5863;
  margin-top: 0.75rem;
  font-size: 14px;
  line-height: 1.313rem;
  font-weight: 400;
}

.dealer-location {
  font-size: 16px;
  color: #4b5863;

  .dealer-name {
    font-weight: 700;
    color: #19222b;
  }
}
</style>
